import BaseController from "confinus_ui/base_controller";
import {
  addParametersToQueryString,
  removeParameterFromQueryString,
} from "lib/util/add_parameter_to_url";
import { Keys, keyWasPressed } from "lib/util/keyboard_key";
import flatpickr from "flatpickr";
import { Instance } from "flatpickr/dist/types/instance";
import DropdownController from "confinus_ui/dropdown_controller";
import FormFieldController from "confinus_ui/forms/form_field_controller";
import CheckboxController from "confinus_ui/forms/checkbox_controller";
import {
  ConfinusUI__DropdownControllerIdentifier,
  ConfinusUI__Forms__CheckboxControllerIdentifier,
  ConfinusUI__Forms__SelectControllerIdentifier,
  ConfinusUI__Forms__TextFieldControllerIdentifier,
} from "controllers/identifiers";
import SelectController from "./forms/select_controller";
import TextFieldController from "./forms/text_field_controller";

export default class SearchAndFilterController extends BaseController {
  public static readonly targets = [
    "searchInput",
    "applyButton",
    "clearFiltersButton",
  ];

  declare public readonly searchInputTarget: HTMLInputElement;
  declare public readonly hasSearchInputTarget: boolean;
  declare public readonly applyButtonTarget: HTMLButtonElement;
  declare public readonly hasApplyButtonTarget: boolean;
  declare public readonly clearFiltersButtonTarget: HTMLButtonElement;
  declare public readonly hasClearFiltersButtonTarget: boolean;

  public static readonly outlets = [
    ConfinusUI__Forms__CheckboxControllerIdentifier,
    ConfinusUI__Forms__SelectControllerIdentifier,
    ConfinusUI__Forms__TextFieldControllerIdentifier,
    ConfinusUI__DropdownControllerIdentifier,
  ];
  declare private readonly confinusUiDropdownOutlet: DropdownController;
  declare private readonly confinusUiFormsCheckboxOutlets: CheckboxController[];
  declare private readonly confinusUiFormsSelectOutlets: SelectController[];
  declare private readonly confinusUiFormsTextFieldOutlets: TextFieldController[];
  private get dropdownController() {
    return this.confinusUiDropdownOutlet;
  }
  private get checkboxControllers() {
    return this.confinusUiFormsCheckboxOutlets;
  }
  private get selectControllers() {
    return this.confinusUiFormsSelectOutlets;
  }
  private get textFieldControllers() {
    return this.confinusUiFormsTextFieldOutlets;
  }

  protected filters: { [key: string]: string | undefined } = {};

  public toggle(event: Event) {
    this.dropdownController.toggle(event);
    event.stopPropagation();
  }

  public hide() {
    this.dropdownController.hide();
  }

  public hideOnClickOutside(event: Event) {
    this.dropdownController.hideOnClickOutside(event);
  }

  public handleClearFilters() {
    this.getFilterValues();
    if (this.hasClearFiltersButtonTarget) {
      this.clearFiltersButtonTarget.disabled = true;
    }
    // Clear the filters
    this.filters = Object.entries(this.filters).reduce<any>(
      (acc, [name, _value]) => {
        acc[name] = undefined;
        return acc;
      },
      {},
    );
    this.reloadAndSetQueryParams();
  }

  public handleApply() {
    this.getFilterValues();
    if (this.hasApplyButtonTarget) {
      this.applyButtonTarget.disabled = true;
    }
    this.reloadAndSetQueryParams();
  }

  protected getFilterValues() {
    if (this.hasSearchInputTarget && this.searchInputTarget.value) {
      const { name, value } = this.searchInputTarget;
      this.addFilterParam(name, value);
    }
    this.checkboxControllers.forEach((controller) => {
      this.addFilterParam(
        controller.name,
        controller.checked ? "true" : undefined,
      );
    });
    this.selectControllers.forEach((controller) => {
      this.addFilterParam(controller.name, controller.value);
    });
    this.textFieldControllers.forEach((controller) => {
      this.addFilterParam(controller.name, controller.value);
    });
  }

  public handleRangePicker(evt: Event) {
    const datePicker = flatpickr(evt.target as HTMLInputElement, {
      mode: "range",
      onClose: (selectedDates: Date[], dateStr: string, instance: Instance) => {
        if (selectedDates.length == 2) {
          this.addFilterParam(
            "custom_range",
            selectedDates
              .map((d) => {
                return instance.formatDate(new Date(d), "Y-m-d");
              })
              .join("_"),
          );
        }
      },
    });
    datePicker.open();
  }

  // https://medium.com/@uistephen/keyboardevent-key-for-cross-browser-key-press-check-61dbad0a067a
  public handleSearchInputKeydown(evt: KeyboardEvent) {
    if (evt.defaultPrevented) {
      return;
    }
    const enterPressed = keyWasPressed(evt, Keys.Enter);
    if (enterPressed) {
      // Stop form submission
      evt.preventDefault();

      this.searchInputTarget.disabled = true;
      this.handleApply();
    }
  }

  protected addFilterParam(name: string, value?: string) {
    this.filters[name] = value;
  }

  protected reloadAndSetQueryParams() {
    // Reset to page 1
    this.filters["page"] = undefined;
    window.location.search = Object.entries(this.filters).reduce(
      (params, filter) => {
        const [name, value] = filter;
        if (value === undefined) {
          return removeParameterFromQueryString(params, name);
        } else {
          return addParametersToQueryString(params, {
            [name]: value,
          });
        }
      },
      window.location.search,
    );
  }
}
